import { createContext, useContext, useCallback, type ReactNode } from "react";
import { useAuth } from "./auth";
import { apiRequest, queryClient } from "./queryClient";

type SupportModeState = {
  active: boolean;
  workspaceId: string | null;
  workspaceName: string | null;
};

type SupportModeContextType = SupportModeState & {
  enterSupportMode: (workspaceId: string) => Promise<void>;
  exitSupportMode: () => Promise<void>;
};

const SupportModeContext = createContext<SupportModeContextType>({
  active: false,
  workspaceId: null,
  workspaceName: null,
  enterSupportMode: async () => {},
  exitSupportMode: async () => {},
});

export function SupportModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const supportMode = user?.supportMode;
  const active = !!supportMode;
  const workspaceId = supportMode?.workspaceId ?? null;
  const workspaceName = supportMode?.workspaceName ?? null;

  const enterSupportMode = useCallback(async (wsId: string) => {
    try {
      await apiRequest("POST", "/api/admin/support-mode/enter", { workspaceId: wsId });
    } catch (err) {
      console.error("[Support Mode] Failed to enter:", err);
      throw err;
    }
    queryClient.clear();
    await queryClient.refetchQueries({ queryKey: ["/api/auth/me"] });
    try { localStorage.removeItem("octa-active-entity"); } catch {}
    window.location.href = "/";
  }, []);

  const exitSupportMode = useCallback(async () => {
    try {
      await apiRequest("POST", "/api/admin/support-mode/exit");
    } catch (err) {
      console.error("[Support Mode] Failed to exit:", err);
      throw err;
    }
    queryClient.clear();
    await queryClient.refetchQueries({ queryKey: ["/api/auth/me"] });
    window.location.href = "/admin";
  }, []);

  return (
    <SupportModeContext.Provider value={{ active, workspaceId, workspaceName, enterSupportMode, exitSupportMode }}>
      {children}
    </SupportModeContext.Provider>
  );
}

export function useSupportMode() {
  return useContext(SupportModeContext);
}
